import { useEffect, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import apiClient from "../../Api/apiConfig";

const columnHelper = createColumnHelper();

export default function ThemesTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null); // Pour gérer la modification
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeDescription, setNewThemeDescription] = useState('');

  const API_URL = 'https://api.nms06.com/api/themes';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error('Échec de la récupération des données');
        }
        const themes = await response.json();
        setData(themes);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEditTheme = async () => {
    try {
      const response = await fetch(`${API_URL}/${selectedTheme.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newThemeName,
          description: newThemeDescription,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la modification du thème');
      }

      const updatedTheme = await response.json();
      setData((prev) =>
        prev.map((theme) =>
          theme.id === updatedTheme.id ? updatedTheme : theme
        )
      );

      setIsEditPopupOpen(false);
      alert('Thème modifié avec succès !');
    } catch (err) {
      console.error(err.message);
      alert('Erreur lors de la modification du thème.');
    }
  };

  const handleDeleteTheme = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce thème ?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression du thème');
      }

      setData((prev) => prev.filter((theme) => theme.id !== id));
      alert('Thème supprimé avec succès !');
    } catch (err) {
      console.error(err.message);
      alert('Erreur lors de la suppression du thème.');
    }
  };

  const openEditPopup = (theme) => {
    setSelectedTheme(theme);
    setNewThemeName(theme.name);
    setNewThemeDescription(theme.description);
    setIsEditPopupOpen(true);
  };

  const table = useReactTable({
    data,
    columns: [
      columnHelper.accessor('name', {
        header: 'Nom',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => (
          <div className="flex space-x-2">
            <button
              onClick={() => openEditPopup(info.row.original)}
              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-700"
            >
              Modifier
            </button>
            <button
              onClick={() => handleDeleteTheme(info.row.original.id)}
              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-700"
            >
              Supprimer
            </button>
          </div>
        ),
      }),
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return <p>Chargement des thèmes...</p>;
  }

  if (error) {
    return <p className="text-red-500">Erreur : {error}</p>;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Gestion des thèmes</h2>
      <button
        onClick={() => alert('Ajout de thème non implémenté')}
        className="mb-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-700"
      >
        Ajouter un thème
      </button>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 border border-gray-300 text-left text-gray-700"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-2 border border-gray-300 text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isEditPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-bold mb-4">Modifier un thème</h3>
            <input
              type="text"
              value={newThemeName}
              onChange={(e) => setNewThemeName(e.target.value)}
              placeholder="Nom"
              className="mb-4 p-2 border rounded w-full"
            />
            <textarea
              value={newThemeDescription}
              onChange={(e) => setNewThemeDescription(e.target.value)}
              placeholder="Description"
              className="mb-4 p-2 border rounded w-full"
            />
            <button
              onClick={handleEditTheme}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700"
            >
              Enregistrer
            </button>
            <button
              onClick={() => setIsEditPopupOpen(false)}
              className="ml-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
